/**
 * Policies & Safety — the deterministic safety-policy rules (read-only) plus
 * the tenant's Safe Mode state and its controls.
 *
 * Reads GET /api/agentic/policies (backend/app/routes/agentic_governance.py)
 * and GET /api/agentic/safe-mode (shared `useSafeMode`). Safe-Mode controls
 * (components/SafeModeControls.tsx) wire POST /safe-mode/enter, the
 * dual-control exit request (POST /config-approvals safe_mode_exit) and the
 * exit execution (POST /safe-mode/exit against an approved authorization).
 * Policy rules themselves stay read-only. On 403/404 → NotEnabled.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Heading,
  Icon,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import { getPolicies, type SafetyPolicy } from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';
import SafeModeControls from '../components/SafeModeControls';
import { SafeModeBadge } from '../components/safeMode';
import { useSafeMode } from '../components/useSafeMode';

const SEVERITY_BADGE: Record<string, 'danger' | 'warning' | 'neutral'> = {
  critical: 'danger',
  high: 'warning',
  medium: 'neutral',
};

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function PoliciesPage() {
  const policiesQ = useQuery({ queryKey: ['agentic', 'policies'], queryFn: getPolicies });
  const safeMode = useSafeMode();

  if (policiesQ.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="policies-loading">
        <Spinner size="lg" label="Loading policies" />
      </div>
    );
  }

  if (policiesQ.error) {
    if (isNotEntitled(policiesQ.error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load policies"
          description="Something went wrong while loading safety policies. Try again."
          action={<Button onClick={() => void policiesQ.refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!policiesQ.data) return <NotEnabled />;

  const { policies } = policiesQ.data;
  const { status, state } = safeMode;

  const shieldIcon =
    status === 'active' ? ShieldAlert : status === 'inactive' ? ShieldCheck : ShieldQuestion;
  const shieldClass =
    status === 'active' ? 'text-danger' : status === 'inactive' ? 'text-success' : 'text-muted';

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <Stack gap="lg">
        <PageHeader
          title="Policies & Safety"
          description="The deterministic safety-policy rules that gate agent learning and actions (read-only), plus your tenant's Safe Mode — the emergency brake and its dual-control exit."
          actions={<SafeModeBadge status={status} />}
        />

        <Section>
          <Card padding="md">
            <Stack gap="sm">
              <div className="flex items-center gap-2">
                <Icon as={shieldIcon} size="sm" className={shieldClass} />
                <Heading level={2} className="text-base">
                  Safe Mode
                </Heading>
              </div>
              {status === 'active' && state ? (
                <Text variant="body-sm" tone="danger">
                  Entered {formatWhen(state.entered_at)}
                  {state.reason ? ` — ${state.reason}` : ''}. Agent actions are halted. Exiting
                  is dual control: request an exit, have a different approver approve it in
                  Config Approvals, then execute it here.
                </Text>
              ) : status === 'inactive' ? (
                <Text variant="body-sm" tone="muted">
                  Not active. Agents operate under the policy rules below. Anyone with propose
                  rights can enter Safe Mode immediately; exiting requires a second approver.
                  {state?.last_exited_at
                    ? ` Last exited ${formatWhen(state.last_exited_at)}.`
                    : ''}
                </Text>
              ) : status === 'loading' ? (
                <Text variant="body-sm" tone="muted">
                  Checking Safe Mode state…
                </Text>
              ) : status === 'unknown' ? (
                <Text variant="body-sm" tone="muted">
                  The Safe Mode state could not be loaded. Treat agent actions as unverified until
                  it can be confirmed.
                </Text>
              ) : null}
              <SafeModeControls status={status} state={state} />
            </Stack>
          </Card>
        </Section>

        <Section>
          <Stack gap="sm">
            <Heading level={2} className="text-base">
              Safety policy rules
            </Heading>
            {policies.length === 0 ? (
              <Card padding="lg">
                <EmptyState
                  compact
                  icon={<Icon as={ShieldCheck} size="lg" />}
                  title="No safety policies configured"
                  description="The deterministic policy filter's rules for this tenant appear here once configured."
                />
              </Card>
            ) : (
              <Card padding="none">
                <ul className="divide-y divide-border" data-testid="policy-list">
                  {policies.map((p: SafetyPolicy) => (
                    <li key={p.rule_id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <Badge variant={SEVERITY_BADGE[p.severity] ?? 'neutral'} size="sm">
                        {p.severity}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <Text variant="body-sm" className="font-medium">
                          {humanize(p.rule_id)}
                        </Text>
                        <Text variant="caption" tone="muted">
                          {humanize(p.rule_category)} · {humanize(p.violation_behavior)}
                          {p.regulatory_ref ? ` · ${p.regulatory_ref}` : ''}
                        </Text>
                      </div>
                      {!p.active && (
                        <Badge variant="neutral" size="sm">
                          inactive
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </Stack>
        </Section>
      </Stack>
    </div>
  );
}
